import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Coins, Rocket, TrendingUp, Shield, Users, Gift,
  ArrowRight, Check, Star, Zap, Clock, ChevronLeft,
  Gem, Trophy, Target, BarChart3, Lock, Sparkles, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const CryptoLaunch = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [tokenStats, setTokenStats] = useState(null);

  // Fetch live token stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/token/stats`);
        if (response.ok) {
          const data = await response.json();
          setTokenStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch token stats:', error);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  // Countdown timer to August 1, 2027
  useEffect(() => {
    const launchDate = new Date('2027-08-01T00:00:00');
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = launchDate - now;
      
      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setCountdown({ days, hours, minutes, seconds });
      }
    };
    
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleJoinWaitlist = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/api/crypto/waitlist', { email });
      setJoined(true);
      toast.success('Welcome to the KBTCOIN waitlist! You will receive updates via email.');
    } catch (error) {
      if (error.response?.data?.status === 'existing') {
        toast.info('You are already on the waitlist!');
        setJoined(true);
      } else {
        setJoined(true);
        toast.success('Welcome to the KBTCOIN waitlist!');
      }
    } finally {
      setLoading(false);
    }
  };

  const tokenomics = [
    { label: 'Total Supply', value: '100,000,000', detail: 'Fixed supply - no inflation' },
    { label: 'Seed Round', value: '10%', detail: '10M tokens at early pricing' },
    { label: 'Trader Rewards', value: '25%', detail: 'Earned through funded accounts' },
    { label: 'Community Reserve', value: '20%', detail: 'Platform growth & rewards' },
    { label: 'Team & Advisors', value: '15%', detail: '3-year vesting schedule' },
    { label: 'Liquidity Pool', value: '30%', detail: 'Market stability & trading' },
  ];

  const earnOpportunities = [
    { 
      account: '$50K Funded Instant', 
      coins: 10, 
      icon: '🥉',
      description: 'Entry level token allocation'
    },
    { 
      account: '$100K Funded Instant', 
      coins: 20, 
      icon: '🥈',
      description: 'Double the rewards'
    },
    { 
      account: '$200K Funded Instant', 
      coins: 40, 
      icon: '🥇',
      description: 'Maximum token allocation'
    },
  ];

  const roadmap = [
    { phase: 'Q1 2026', title: 'Platform Launch', items: ['TRADERSCAPITAL goes live', 'Funded account program starts', 'Community building begins'], status: 'completed' },
    { phase: 'Q2 2026', title: 'Seed Round Opens', items: ['Early investor access', 'Token presale begins', 'Partnership announcements'], status: 'active' },
    { phase: 'Q4 2026', title: 'Token Utility', items: ['KBTCOIN rewards go live', 'Staking program launches', 'Governance features'], status: 'upcoming' },
    { phase: 'Q3 2027', title: 'Public Launch', items: ['Exchange listings', 'Full trading enabled', 'Global expansion'], status: 'upcoming' },
  ];

  const valueDrivers = [
    {
      icon: TrendingUp,
      title: 'Challenge Sales',
      description: 'Token value increases as more traders purchase challenges and funded accounts'
    },
    {
      icon: BarChart3,
      title: 'Trading Profits',
      description: 'Revenue from successful Elite Funded Trader trading contributes to token backing'
    },
    {
      icon: Users,
      title: 'Community Growth',
      description: 'Growing member base creates organic demand for KBTCOIN tokens'
    },
    {
      icon: Shield,
      title: 'Business Value',
      description: 'Token price reflects overall platform success and capital growth'
    },
  ];

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-24 pb-16">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-gray-400 hover:text-white"
          data-testid="back-to-home"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Launch Badge */}
            <Badge className="mb-6 px-5 py-2 bg-yellow-500/10 backdrop-blur-sm border border-yellow-500/30 text-yellow-300 text-sm font-medium">
              <Rocket className="w-4 h-4 mr-2" />
              LAUNCHING AUGUST 2027
            </Badge>

            {/* Token Logo */}
            <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center shadow-2xl shadow-yellow-500/30">
              <Coins className="w-16 h-16 text-white" />
            </div>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-4" data-testid="crypto-title">
              <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-yellow-400 bg-clip-text text-transparent">
                KBTCOIN
              </span>
            </h1>
            
            {/* ERC-20 Badge */}
            <div className="flex justify-center gap-3 mb-4">
              <Badge className="px-4 py-1 bg-purple-500/10 text-purple-400 border border-purple-500/20">
                ERC-20 Token
              </Badge>
              <Badge className="px-4 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20">
                Ethereum Blockchain
              </Badge>
            </div>
            
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-6">
              The exclusive utility token of <span className="text-yellow-400 font-bold">Knowledge Based Trading</span> ecosystem.
              Earn, trade, and grow with our community of successful traders.
            </p>

            {/* Countdown Timer */}
            <div className="max-w-xl mx-auto mb-8" data-testid="countdown-timer">
              <p className="text-gray-500 text-sm mb-3">LAUNCH COUNTDOWN</p>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-white/5 rounded-xl p-4 border border-yellow-500/20">
                  <p className="text-3xl sm:text-4xl font-bold text-yellow-400">{countdown.days}</p>
                  <p className="text-xs text-gray-500 uppercase">Days</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 border border-yellow-500/20">
                  <p className="text-3xl sm:text-4xl font-bold text-yellow-400">{countdown.hours}</p>
                  <p className="text-xs text-gray-500 uppercase">Hours</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 border border-yellow-500/20">
                  <p className="text-3xl sm:text-4xl font-bold text-yellow-400">{countdown.minutes}</p>
                  <p className="text-xs text-gray-500 uppercase">Minutes</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 border border-yellow-500/20">
                  <p className="text-3xl sm:text-4xl font-bold text-yellow-400">{countdown.seconds}</p>
                  <p className="text-xs text-gray-500 uppercase">Seconds</p>
                </div>
              </div>
            </div>

            {/* Waitlist Form or Buy Now CTA */}
            {tokenStats?.seed_round_open ? (
              <div className="max-w-md mx-auto space-y-4">
                <Button
                  onClick={() => navigate('/kbtcoin')}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-bold rounded-full px-6 py-6 text-lg"
                  data-testid="buy-tokens-btn"
                >
                  <Coins className="w-5 h-5 mr-2" />
                  Buy KBTCOIN Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-gray-500 text-sm">Seed round is now open! {tokenStats.seed_round_remaining?.toLocaleString()} tokens remaining.</p>
              </div>
            ) : !joined ? (
              <form onSubmit={handleJoinWaitlist} className="max-w-md mx-auto">
                <div className="flex gap-3">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    data-testid="waitlist-email"
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-bold rounded-full px-6"
                    data-testid="join-waitlist-btn"
                  >
                    {loading ? 'Joining...' : 'Join Waitlist'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 max-w-md mx-auto"
              >
                <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
                <p className="text-green-400 font-bold text-lg">You're on the list!</p>
                <p className="text-gray-400 text-sm mt-2">We'll notify you when the seed round opens.</p>
              </motion.div>
            )}

            {/* Quick Stats - Live Data */}
            <div className="flex justify-center gap-8 mt-12" data-testid="live-stats">
              <div className="text-center">
                <p className="text-3xl font-bold text-white">{tokenStats ? (tokenStats.total_supply / 1000000).toFixed(0) + 'M' : '100M'}</p>
                <p className="text-gray-500 text-sm">Total Supply</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-yellow-400">{tokenStats ? tokenStats.tokens_in_circulation.toLocaleString() : '0'}</p>
                <p className="text-gray-500 text-sm">In Circulation</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white">£{tokenStats ? tokenStats.token_price_gbp.toFixed(2) : '0.10'}</p>
                <p className="text-gray-500 text-sm">Token Price</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-400">{tokenStats?.seed_round_open ? 'OPEN' : 'CLOSED'}</p>
                <p className="text-gray-500 text-sm">Seed Round</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Exclusive Announcement */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-yellow-500/10 rounded-3xl p-8 md:p-12 border border-yellow-500/20"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Sparkles className="w-8 h-8 text-yellow-400" />
              <h2 className="font-heading text-3xl font-bold text-white">EXCLUSIVE OPPORTUNITY</h2>
              <Sparkles className="w-8 h-8 text-yellow-400" />
            </div>
            <p className="text-gray-300 text-center max-w-3xl mx-auto text-lg leading-relaxed">
              KBTCOIN is <span className="text-yellow-400 font-bold">NOT</span> just another cryptocurrency. 
              It's an <span className="text-yellow-400 font-bold">ERC-20 utility token</span> backed by real business value. 
              As TRADERSCAPITAL grows, so does the value of your tokens. Early supporters get the best pricing 
              during our seed round before the public launch.
            </p>
            
            {/* Whitepaper Link */}
            <div className="mt-8 flex justify-center">
              <a 
                href="/whitepaper.html" 
                target="_blank"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-full px-6 py-3 border border-yellow-500/20 hover:border-yellow-500/40 transition-all hover:scale-105"
                data-testid="whitepaper-link"
              >
                <FileText className="w-5 h-5 text-yellow-400" />
                <span className="text-white font-medium">Whitepaper (Investors & Exchanges)</span>
                <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">v1.0</Badge>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Live Seed Round Progress */}
      {tokenStats && (
        <section className="py-8">
          <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-[hsl(220,20%,7%)] border-yellow-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-yellow-400" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-white">Seed Round Progress</h3>
                        <p className="text-sm text-gray-500">Live allocation status</p>
                      </div>
                    </div>
                    <Badge className={tokenStats.seed_round_open ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}>
                      {tokenStats.seed_round_open ? 'Open' : 'Closed'}
                    </Badge>
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Tokens Sold</span>
                      <span className="text-white font-mono">{tokenStats.seed_round_sold?.toLocaleString()} / {tokenStats.seed_round_total?.toLocaleString()} KBT</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-yellow-400 to-orange-500 h-3 rounded-full transition-all"
                        style={{ width: `${tokenStats.seed_round_progress_pct || 0}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs mt-1">
                      <span className="text-gray-500">{tokenStats.seed_round_progress_pct?.toFixed(2)}% sold</span>
                      <span className="text-gray-500">{tokenStats.seed_round_remaining?.toLocaleString()} remaining</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <p className="text-yellow-400 font-mono font-bold">£{tokenStats.token_price_gbp?.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">Price per KBT</p>
                    </div>
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <p className="text-cyan-400 font-mono font-bold">{tokenStats.tokens_in_circulation?.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">In Circulation</p>
                    </div>
                    <div className="text-center p-3 bg-white/5 rounded-lg">
                      <p className="text-white font-mono font-bold">£{((tokenStats.seed_round_sold || 0) * (tokenStats.token_price_gbp || 0.10)).toFixed(2)}</p>
                      <p className="text-xs text-gray-500">Total Raised</p>
                    </div>
                  </div>
                  
                  {tokenStats.seed_round_open && (
                    <Button
                      onClick={() => navigate('/kbtcoin')}
                      className="w-full mt-4 bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-bold"
                      data-testid="buy-now-progress-btn"
                    >
                      <Coins className="w-4 h-4 mr-2" />
                      Buy KBTCOIN Now
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      )}

      {/* Earn KBTCOIN Section */}
      <section className="py-12 sm:py-20 bg-[hsl(220,20%,6%)]" data-testid="earn-section">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 px-4 py-1 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
              <Gift className="w-4 h-4 mr-2" />
              TRADER REWARDS
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              EARN <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">KBTCOIN</span> BY TRADING
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Purchase Funded Instant accounts ($50K, $100K, $200K) and receive KBTCOIN tokens with every purchase.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {earnOpportunities.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-[hsl(220,20%,7%)] border border-white/10 hover:border-yellow-500/30 transition-all h-full">
                  <CardContent className="p-8 text-center">
                    <div className="text-6xl mb-4">{item.icon}</div>
                    <h3 className="font-heading text-xl font-bold text-white mb-2">{item.account}</h3>
                    <div className="flex items-center justify-center gap-2 mb-4">
                      <Coins className="w-6 h-6 text-yellow-400" />
                      <span className="text-4xl font-black text-yellow-400">{item.coins}</span>
                      <span className="text-gray-400">KBTCOIN</span>
                    </div>
                    <p className="text-gray-500 text-sm">{item.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-gray-500 mt-8">
            Tokens awarded on each purchase - the more you trade, the more you earn!
          </p>
        </div>
      </section>

      {/* Value Drivers */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 px-4 py-1 bg-green-500/10 text-green-400 border border-green-500/20">
              <TrendingUp className="w-4 h-4 mr-2" />
              VALUE GROWTH
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              WHAT DRIVES <span className="text-yellow-400">TOKEN VALUE</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              KBTCOIN value is directly tied to the success of TRADERSCAPITAL platform.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {valueDrivers.map((driver, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-[hsl(220,20%,7%)] border border-white/10 hover:border-green-500/30 transition-all h-full">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                      <driver.icon className="w-7 h-7 text-green-400" />
                    </div>
                    <h3 className="font-heading text-lg font-bold text-white mb-2">{driver.title}</h3>
                    <p className="text-gray-500 text-sm">{driver.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tokenomics */}
      <section className="py-12 sm:py-20 bg-[hsl(220,20%,6%)]" data-testid="tokenomics-section">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 px-4 py-1 bg-purple-500/10 text-purple-400 border border-purple-500/20">
              <Gem className="w-4 h-4 mr-2" />
              TOKENOMICS
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              TOKEN <span className="text-purple-400">DISTRIBUTION</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tokenomics.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-[hsl(220,20%,7%)] border border-white/10 hover:border-purple-500/30 transition-all">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-gray-400">{item.label}</p>
                      <p className="font-heading text-2xl font-bold text-purple-400">{item.value}</p>
                    </div>
                    <p className="text-gray-500 text-sm">{item.detail}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 px-4 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20">
              <Target className="w-4 h-4 mr-2" />
              ROADMAP
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              OUR <span className="text-blue-400">JOURNEY</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {roadmap.map((phase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className={`h-full border ${
                  phase.status === 'completed' ? 'bg-green-500/5 border-green-500/30' :
                  phase.status === 'active' ? 'bg-yellow-500/5 border-yellow-500/30' :
                  'bg-[hsl(220,20%,7%)] border-white/10'
                }`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Badge className={`${
                        phase.status === 'completed' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                        phase.status === 'active' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                        'bg-white/10 text-gray-400 border-white/10'
                      }`}>
                        {phase.phase}
                      </Badge>
                      {phase.status === 'completed' && <Check className="w-5 h-5 text-green-400" />}
                      {phase.status === 'active' && <Zap className="w-5 h-5 text-yellow-400" />}
                      {phase.status === 'upcoming' && <Clock className="w-5 h-5 text-gray-500" />}
                    </div>
                    <h3 className="font-heading text-lg font-bold text-white mb-3">{phase.title}</h3>
                    <ul className="space-y-2">
                      {phase.items.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                          <div className={`w-1.5 h-1.5 rounded-full ${
                            phase.status === 'completed' ? 'bg-green-400' :
                            phase.status === 'active' ? 'bg-yellow-400' :
                            'bg-gray-600'
                          }`} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-yellow-500 to-orange-600 flex items-center justify-center shadow-lg shadow-yellow-500/30">
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              BE AN <span className="text-yellow-400">EARLY SUPPORTER</span>
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              Join the waitlist now to get exclusive access to the seed round when it opens.
              Early investors receive the best token pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => tokenStats?.seed_round_open ? navigate('/kbtcoin') : navigate('/get-funded')}
                className="bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-heading uppercase tracking-wider rounded-full px-10 py-7 shadow-lg shadow-yellow-500/25"
                data-testid="start-earning-btn"
              >
                {tokenStats?.seed_round_open ? 'Buy KBTCOIN Now' : 'Start Earning KBTCOIN'}
                <Coins className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="border-white/20 text-white hover:bg-white/5 rounded-full px-10 py-7"
              >
                {tokenStats?.seed_round_open ? 'View Seed Round' : 'Join Waitlist'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CryptoLaunch;
